import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';
import { ArrowLeftShort, ArrowRightShort, Folder2Open, GearFill, PersonXFill, Search } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { Skeleton } from '@mui/material';
import { Dropdown } from 'rsuite';
import { observer } from 'mobx-react-lite';
import context, { IClientsTreeNode } from './context/shared';
import { NodeTypes } from '../variables';
import { getNodeButtons } from './ClientsTreeButtonPanel';
import AddAccountForm from './forms/AddAccountForm';
import { findAccountNodeByLoginAndClient, getClientNodeById, searchAccountsInTree } from '../../utils/treeFunctions';
import type { Account } from '../../shared/generated';
import { useBackButton } from '../../utils/backButton';

const ROW_HEIGHT = 56;

interface IMobileClientsListItem {
    node: IClientsTreeNode;
    isAccount: boolean;
    viaSearch: boolean;
    clientName?: string;
}

interface IMobileClientsListProps {
    showTopButtons: boolean;
    selectFunc?: (node: IClientsTreeNode) => void;
    selectedKey?: string;
}

const getDevicesCount = (node: IClientsTreeNode) => {
    const match = /\((\d+)\)$/.exec(node.label ?? '');
    return match ? match[1] : undefined;
};

const MobileClientsList = observer((props: IMobileClientsListProps) => {
    const { showTopButtons, selectFunc, selectedKey } = props;
    const { clientsTreeData, isClientsTreeDataLoading, currentNode, setCurrentNode } = context;

    const [activeClientLocal, setActiveClientLocal] = useState<IClientsTreeNode | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [height, setHeight] = useState(300);
    const listContainerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<VirtualList | null>(null);

    const sizeToFit = useCallback(() => {
        const newHeight = listContainerRef?.current?.getBoundingClientRect().height || 300;
        setHeight(Math.max(newHeight));
    }, []);

    useEffect(() => {
        sizeToFit();
        window.addEventListener('resize', sizeToFit);
        return () => window.removeEventListener('resize', sizeToFit);
    }, [sizeToFit]);

    const rootClients = useMemo<IClientsTreeNode[]>(() => {
        const root = clientsTreeData?.[0];
        return (root?.children as IClientsTreeNode[] | undefined) ?? [];
    }, [clientsTreeData]);

    // в режиме Administration уровень навигации определяется currentNode,
    // в диалоге выбора — локальным состоянием
    const activeClient = useMemo(() => {
        if (!showTopButtons)
            return activeClientLocal;
        if (!currentNode || currentNode.type === NodeTypes.root)
            return null;
        if (currentNode.type === NodeTypes.client)
            return getClientNodeById(currentNode.id, clientsTreeData);
        if (currentNode.type === NodeTypes.account)
            return getClientNodeById(currentNode.parentId, clientsTreeData);
        return null;
    }, [showTopButtons, currentNode, clientsTreeData, activeClientLocal]);

    const accounts = useMemo<IClientsTreeNode[]>(
        () => (activeClient?.children as IClientsTreeNode[] | undefined) ?? [],
        [activeClient]
    );

    // поиск: на уровне логинов фильтрует текущий список,
    // на уровне клиентов ищет клиентов по имени + логины по всему дереву
    const items = useMemo<IMobileClientsListItem[]>(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        if (activeClient) {
            const source = (accounts as IClientsTreeNode[])
                .filter(node => !normalizedQuery || (node.name ?? '').toLowerCase().includes(normalizedQuery));
            return source.map(node => ({ node, isAccount: node.type === NodeTypes.account, viaSearch: false }));
        }
        const clientItems = rootClients
            .filter(node => !normalizedQuery || (node.name ?? '').toLowerCase().includes(normalizedQuery))
            .map(node => ({ node, isAccount: false, viaSearch: false }));
        if (!normalizedQuery)
            return clientItems;
        const foundLogins = searchAccountsInTree(searchQuery, clientsTreeData)
            .filter(found => found.client != null)
            .map(found => ({
                node: found.account,
                isAccount: true,
                viaSearch: true,
                clientName: found.client?.name
            }));
        return [...clientItems, ...foundLogins];
    }, [activeClient, accounts, rootClients, searchQuery, clientsTreeData]);

    const listItems = activeClient || !searchQuery.trim() ? items : [];
    const searchAccountsResult = !activeClient && searchQuery.trim() ? items : [];
    const { clientAccounts, clientAccountsClientId } = context;
    const accountsData = useMemo<Account[]>(
        () => activeClient && clientAccountsClientId === activeClient.id ? clientAccounts : [],
        [activeClient, clientAccounts, clientAccountsClientId]
    );

    const getAccountPassword = (node: IClientsTreeNode) => {
        const account = accountsData.find(acc => acc.Login === node.name);
        return account?.Password ?? '';
    };

    const onSearchAccountClick = (item: IMobileClientsListItem) => {
        if (item.node.type === NodeTypes.account) {
            if (showTopButtons)
                setCurrentNode(item.node);
            else
                selectFunc?.(item.node);
            setSearchQuery('');
            return;
        }
        // клиенты из результатов поиска — переход внутрь клиента
        if (showTopButtons)
            setCurrentNode(item.node);
        else {
            setActiveClientLocal(item.node);
            selectFunc?.(item.node);
        }
        setSearchQuery('');
    };

    const selectedAccountKey = showTopButtons
        ? currentNode?.type === NodeTypes.account ? currentNode.key : undefined
        : selectedKey;

    // при выбранном аккаунте список скрывается — контроллеры занимают всю высоту
    const isAccountSelected = showTopButtons && currentNode?.type === NodeTypes.account;

    const onClientClick = (client: IClientsTreeNode) => {
        if (showTopButtons)
            setCurrentNode(client);
        else {
            setActiveClientLocal(client);
            selectFunc?.(client);
        }
    };

    const onAccountClick = (account: IClientsTreeNode) => {
        if (showTopButtons)
            setCurrentNode(account);
        else
            selectFunc?.(account);
    };

    const onBack = () => {
        if (showTopButtons) {
            if (currentNode?.type === NodeTypes.account)
                setCurrentNode(getClientNodeById(currentNode.parentId, clientsTreeData));
            else
                setCurrentNode(null);
        } else
            setActiveClientLocal(null);
    };

    // при обновлении дерева выбранный нод мог быть удалён — откатываемся на уровень выше
    useEffect(() => {
        if (!showTopButtons || !clientsTreeData || !currentNode)
            return;
        if (currentNode.type === NodeTypes.root) {
            setCurrentNode(null);
            return;
        }
        if (currentNode.type === NodeTypes.account) {
            const account = findAccountNodeByLoginAndClient(currentNode.name, currentNode.parentId, clientsTreeData);
            if (!account)
                setCurrentNode(getClientNodeById(currentNode.parentId, clientsTreeData));
            return;
        }
        if (currentNode.type === NodeTypes.client && getClientNodeById(currentNode.id, clientsTreeData) == null)
            setCurrentNode(null);
    }, [clientsTreeData, currentNode, showTopButtons, setCurrentNode]);

    // держим подсвеченный нод в зоне видимости
    useEffect(() => {
        if (!selectedAccountKey || searchQuery.trim())
            return;
        const list = activeClient ? accounts : rootClients;
        const index = list.findIndex(node => node.key === selectedAccountKey);
        if (index >= 0)
            listRef.current?.scrollToItem(index, 'center');
    }, [activeClient, accounts, rootClients, selectedAccountKey, searchQuery]);

    // системная «Назад»: сначала сбрасываем поиск, затем переходим на уровень выше
    const backActive = showTopButtons ? !!currentNode : !!activeClientLocal;
    useBackButton(() => {
        if (searchQuery.trim()) {
            setSearchQuery('');
            return;
        }
        onBack();
    }, backActive);

    if (!clientsTreeData)
        return <div className="mobileClientsEmpty">Загрузка...</div>;

    const searchInput =
        <div className="mobileClientsSearch">
            <Search className="mobileClientsSearchIcon" />
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={activeClient ? 'Поиск логина...' : 'Поиск клиента или логина...'}
            />
            {searchQuery ?
                <Button variant="transparent" className="mobileClientsSearchClear" title="Очистить" onClick={() => setSearchQuery('')}>
                    ×
                </Button>
                : null}
        </div>;

    const headerTitle = isAccountSelected
        ? `Логин: ${currentNode?.name ?? ''}`
        : activeClient
            ? `Клиент: ${activeClient.name}`
            : 'Клиенты';

    const clientActionsDropdown = showTopButtons && activeClient && !isAccountSelected ?
        <div className="mobileClientsActions">
            <Dropdown title="Действия">
                <AddAccountForm clientId={activeClient.id} showTitle={true} variant="dropdownItem" />
                <Dropdown.Item onClick={context.handleDeleteClient}>
                    <PersonXFill className='mediumSvg marginRight' />
                    Удалить клиента
                </Dropdown.Item>
                <Dropdown.Item onClick={() => context.setShowClientSettings(true)}>
                    <GearFill className='mediumSvg marginRight' />
                    Настройки клиента
                </Dropdown.Item>
            </Dropdown>
        </div>
        : null;

    if (isAccountSelected) {
        return (
            <div className="mobileClientsContainer mobileClientsCompact">
                <div className="mobileClientsHeader">
                    <Button variant="custom" className="mobileClientsBack" title="Назад" onClick={onBack}>
                        <ArrowLeftShort className="mediumSvg" />Назад
                    </Button>
                    <div className="mobileClientsTitleGroup">
                        <button type="button" className="mobileClientsSubtitle" title={`Клиент: ${activeClient?.name ?? ''}`} onClick={onBack}>
                            Клиент: {activeClient?.name ?? ''}
                        </button>
                        <span className="mobileClientsTitle">{headerTitle}</span>
                    </div>
                </div>
            </div>
        );
    }

    const Row = ({ index, style }: { index: number; style: CSSProperties }) => {
        const item = items[index];
        const node = item.node;
        const selected = activeClient != null && node.key === selectedAccountKey;
        const devicesCount = getDevicesCount(node);
        return (
            <button
                type="button"
                className={'mobileClientsRow' + (selected ? ' selected' : '')}
                style={style}
                onClick={() => {
                    if (item.viaSearch)
                        onSearchAccountClick(item);
                    else if (activeClient)
                        onAccountClick(node);
                    else
                        onClientClick(node);
                }}
            >
                <div className="mobileClientsRowMain">
                    <span className="mobileClientsRowTitle">
                        {item.viaSearch ? <span className="mobileClientsRowType login">Логин</span> : null}
                        {node.name ?? node.label}
                    </span>
                    <span className="mobileClientsRowSecondary">
                        {item.viaSearch && item.clientName ?
                            `Клиент: ${item.clientName}` :
                            node.type === NodeTypes.account ? `пароль: ${getAccountPassword(node)}` : `#${node.id}`}
                    </span>
                </div>
                {devicesCount ?
                    <span className="mobileClientsRowSecondary">устройств: {devicesCount}</span> : null}
                <ArrowRightShort className="mobileClientsRowArrow" />
            </button>
        );
    };

    const searchAccountsList =
        <div className="mobileClientsListContainer mobileClientsSearchList">
            {searchAccountsResult.length === 0 ?
                <div className="mobileClientsEmptyState">
                    <Folder2Open className="mobileClientsEmptyStateIcon" />
                    <span>Ничего не найдено</span>
                </div>
                :
                searchAccountsResult.map((item, index) =>
                    <button
                        key={item.node.key ?? index}
                        type="button"
                        className="mobileClientsRow"
                        onClick={() => onSearchAccountClick(item)}
                    >
                        <div className="mobileClientsRowMain">
                            <span className="mobileClientsRowTitle">
                                {item.isAccount ?
                                    <span className="mobileClientsRowType login">Логин</span> :
                                    <span className="mobileClientsRowType client">Клиент</span>}
                                {item.node.name ?? item.node.label}
                            </span>
                            <span className="mobileClientsRowSecondary">
                                {item.isAccount && item.clientName ? `Клиент: ${item.clientName}` : ''}
                            </span>
                        </div>
                        <ArrowRightShort className="mobileClientsRowArrow" />
                    </button>
                )
            }
        </div>;

    return (
        <div className="mobileClientsContainer">
            <div className="mobileClientsHeader">
                {activeClient ?
                    <Button variant="custom" className="mobileClientsBack" title="Назад" onClick={onBack}>
                        <ArrowLeftShort className="mediumSvg" />Назад
                    </Button>
                    : null}
                <span className="mobileClientsTitle">{headerTitle}</span>
            </div>
            {clientActionsDropdown}
            {showTopButtons && currentNode?.type === NodeTypes.root ?
                <div className="mobileClientsToolbar">
                    {getNodeButtons(currentNode?.type, true)}
                </div>
                : null}
            {searchInput}
            {activeClient && !searchQuery.trim() ? <div className="mobileClientsListTitle">Логины</div> : null}
            {activeClient || !searchQuery.trim() ?
                <div ref={listContainerRef} className="mobileClientsListContainer">
                    {!clientsTreeData && isClientsTreeDataLoading ?
                        <Skeleton height={height} />
                        : listItems.length === 0 ?
                            <div className="mobileClientsEmptyState">
                                <Folder2Open className="mobileClientsEmptyStateIcon" />
                                <span>
                                    {activeClient
                                        ? accounts.length > 0 ? 'Ничего не найдено' : 'Нет логинов — добавьте через «Действия»'
                                        : rootClients.length > 0 ? 'Ничего не найдено' : 'Нет клиентов'}
                                </span>
                            </div>
                            :
                            <VirtualList
                                ref={listRef}
                                height={height}
                                itemCount={listItems.length}
                                itemSize={ROW_HEIGHT}
                                width={'100%'}
                            >
                                {Row}
                            </VirtualList>
                    }
                </div>
                : searchAccountsList}
        </div>
    );
});

export default MobileClientsList;